<template>
  <v-card v-if="loading" class="pa-16">
    <h2 class="text-center">
      <v-progress-circular
          class="mr-2"
          color="primary"
          indeterminate
      ></v-progress-circular>
      Loading...
    </h2>
  </v-card>
  <v-card v-else class="pa-2">
    <h3 align="right">Total Staff:
      <v-chip
          class="ma-2"
          color="green"
          small
      >
        <v-icon left small>
          mdi-account-circle-outline
        </v-icon>
        {{ totalStaff }}
      </v-chip>
    </h3>
    <v-row no-gutters>
      <v-col>
        <apexchart
            ref="chart"
            :options="chartOptions"
            :series="ageSeries"
            type="bar"
            width="400"
        ></apexchart>
      </v-col>
      <v-col>
        <template>
          <v-data-table
              :headers="headers"
              :items="desserts"
              class="elevation-1"
              dense
              hide-default-footer
              hide-default-header
          ></v-data-table>
        </template>
      </v-col>
    </v-row>
    <v-row no-gutters>
      <v-col>
        <apexchart
            :key="genderCategoryKey"
            ref="genderChart"
            :options="genderChart"
            :series="genderSeries"
            type="donut"
            width="400"
        ></apexchart>
      </v-col>
      <v-col>
        <apexchart
            :key="professionCategoryKey"
            ref="professionChart"
            :options="professionCategoryOptions"
            :series="professionCategorySeries"
            type="pie"
            width="400"
        ></apexchart>
      </v-col>
    </v-row>
  </v-card>
</template>

<script>
import axios from "axios";
import '../plugins/chart'
import querystring from "querystring";

export default {
  name: "FacilityInformation",
  props: ["id", "name", "partOf", "closed"],
  data: function () {
    return {
      loading: true,
      response: undefined,
      totalStaff: 0,
      series: [],
      genderSeries: [0, 0],
      professionCategorySeries: [0, 0, 0],
      professionCategoryKey: 0,
      genderCategoryKey: 0,
      chartOptions: {
        chart: {
          type: "bar",
        },
        labels: ["20 To 35", "36 To 45", "46 To 55", "56 To 65", "GT 65"],
        grid: {
          row: {
            colors: []
          }
        },
      },
      genderChart: {
        title: {
          text: 'By Gender',
          align: 'center',
          style: {
            fontSize: '14px',
            fontWeight: 'bold',
            fontFamily: undefined,
            color: '#263238'
          },
        },
        labels: ['Male', 'Female'],
        chart: {
          type: 'donut',
        },
        plotOptions: {
          pie: {
            startAngle: -90,
            endAngle: 90,
            offsetY: 10
          }
        },
        grid: {
          padding: {
            bottom: -80
          }
        },
        responsive: [{
          breakpoint: 480,
          options: {
            chart: {
              width: 200
            },
            legend: {
              position: 'bottom'
            }
          }
        }]
      },
      professionCategoryOptions: {
        chart: {
          width: 380,
          type: 'pie',
        },
        labels: ['Professional', 'Administrative', 'Academics'],
        title: {
          text: 'By Profession Category',
          align: 'center',
          style: {
            fontSize: '14px',
            fontWeight: 'bold',
            fontFamily: undefined,
            color: '#263238'
          },
        },
        responsive: [{
          breakpoint: 480,
          options: {
            chart: {
              width: 200
            },
            legend: {
              position: 'bottom'
            }
          }
        }]
      },
      headers: [
        {
          text: 'Name',
          value: 'name',
        },
        {text: 'value', value: 'value'},
      ],
      desserts: [],
      professionCategory: {
        professional: 0,
        administrative: 0,
        academic: 0,
      },
      ageSeries: [],
      ageRangeData: {
        "20to35": 0,
        "36to45": 10,
        "46to55": 0,
        "56to65": 0,
        "gt65": 0,
      },
      source: ""
    }
  },
  created: function () {
    this.setupData();
  },
  watch: {
    id: function () {
      this.setupData();
    },
    partOf: function () {
      this.loading = true;
      this.setupData();
    },
    closed: function () {
      if (this.closed === false) {
        this.response = undefined;
        this.id = undefined;
        this.series = [];
        this.genderSeries = [0, 0];
        this.professionCategorySeries = [0, 0, 0];
      } else {
        this.response = undefined;
        this.series = [];
        this.genderSeries = [0, 0];
        this.professionCategorySeries = [0, 0, 0];
        this.setupData();
      }
    },
    professionCategorySeries: function () {
      this.professionCategoryKey++;
    },
    genderSeries: function () {
      this.genderCategoryKey++;
    }
  },
  methods: {
    async setupData() {
      this.loading = false
      if (this.partOf) {
        this.source = `if(doc['ihris-related-group.keyword'].size() != 0) {if(doc['ihris-related-group.keyword'].value.contains('${this.id.split('/').pop()}')){return true}}`
      } else {
        this.source = `if(!doc['dutyPost.keyword'].empty) {if(doc['dutyPost.keyword'].value == '${this.name}'){return true}}`
      }
      try {
        let url = `/fhir/Location/${this.id.split('/').pop()}`
        let {data} = await axios(url)
        let facilityInformation = [];
        if (data) {
          if (data.partOf) {
            let facilityData = await axios(
                `/fhir/Location/${data.partOf.reference.split("/").pop()}`
            );
            if (facilityData) {
              facilityInformation.push({
                name: "Report To",
                value: facilityData.name,
              });
            }
          }
          facilityInformation.push({
            name: "Name",
            value: data.name,
          });
          facilityInformation.push({
            name: "Status",
            value: data.status,
          });
          let ownership = data.extension.find(
              (x) =>
                  x.url ===
                  "http://ihris.org/fhir/StructureDefinition/ihris-facility-ownership-prefix"
          );
          let region = data.extension.find(
              (x) =>
                  x.url ===
                  "http://ihris.org/fhir/StructureDefinition/ihris-facility-location"
          );
          if (region) {
            let regionUrl = `/fhir/Location/?_include:iterate=Location:partof&_id=${region.valueReference.reference.split("/").pop()}`
            let {data} = await axios(regionUrl);
            if (data && data.entry.length > 0) {
              data.entry.map((d) => {
                if (
                    d.resource.type &&
                    d.resource.type.length > 0 &&
                    d.resource.type[0].coding &&
                    d.resource.type[0].coding.length > 0 &&
                    d.resource.type[0].coding[0].code !== "country"
                ) {
                  facilityInformation.push({
                    name: d.resource.type[0].coding[0].code,
                    value: d.resource.name,
                  });
                }
              });
            }
          }
          if (ownership && ownership.valueCoding) {
            facilityInformation.push({
              name: "Ownership",
              value: ownership.valueCoding.display,
            });
          }
          if (data.type && data.type.length > 0) {
            facilityInformation.push({
              name: "Type",
              value: data.type[0].coding[0].display,
            });
          }
          if (data.physicalType) {
            facilityInformation.push({
              name: "Physical type",
              value: data.physicalType.coding[0].display,
            });
          }
        }
        this.desserts = facilityInformation
      } catch (err) {
        console.log(err);
      }
      let genderQuery = {
        "query": {
          "bool": {
            "must": [
              {
                "script": {
                  "script": {
                    "source": this.source,
                    "lang": "painless"
                  }
                }
              }
            ],
            "must_not": [],
            "filter": []
          }
        },
        "aggs": {
          "genderCategories": {
            "composite": {
              "sources": [
                {
                  "gender": {
                    "terms": {
                      "field": "gender.keyword"
                    }
                  }
                }
              ]
            }
          }
        },
        "reportOptions": {},
      }

      let genderUrl = `/es/jobposition/_search?filter_path=aggregations`
      let gender = {
        male: 0,
        female: 0,
      };
      let response = await fetch(genderUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(genderQuery)
      })
      let result = await response.json()
      result.aggregations.genderCategories.buckets.map((a) => {
        gender[a.key.gender] = a.doc_count
      })
      this.genderSeries = Object.values(gender)

      let categoriesQuery = {
        "query": {
          "bool": {
            "must": [
              {
                "script": {
                  "script": {
                    "source": this.source,
                    "lang": "painless"
                  }
                }
              }
            ],
            "must_not": [],
            "filter": []
          }
        },
        "aggs": {
          "categories": {
            "composite": {
              "sources": [
                {
                  "categorie": {
                    "terms": {
                      "field": "category.keyword"
                    }
                  }
                }
              ]
            }
          }
        },
        "reportOptions": {},
      }

      let categoriesUrl = `/es/jobposition/_search?filter_path=aggregations`

      let categoriesResponse = await fetch(categoriesUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(categoriesQuery)
      })
      let categoriesResult = await categoriesResponse.json()
      categoriesResult.aggregations.categories.buckets.map((a) => {
        let name = a.key.categorie
        let count = a.doc_count
        if (name === 'Academic(Less than Grade 12 education Level)') {
          this.professionCategory.academic = count;
        } else if (name === "Administrative") {
          this.professionCategory.administrative = count
        } else if (name === 'Health Professional') {
          this.professionCategory.professional = count
        }
      })
      this.response = {}
      this.professionCategorySeries = Object.values(this.professionCategory)

      let ageRangeQuery = {
        "query": {
          "bool": {
            "must": [
              {
                "script": {
                  "script": {
                    "source": this.source,
                    "lang": "painless"
                  }
                }
              }
            ],
            "must_not": [],
            "filter": []
          }
        },
        "aggs": {
          "age_range": {
            "range": {
              "field": "birthDate",
              "keyed": true,
              "ranges": [
                {
                  "key": "20to35",
                  "from": "now-35y/y",
                  "to": "now-20y/y"
                },
                {
                  "key": "36to45",
                  "from": "now-45y/y",
                  "to": "now-36y/y"
                },
                {
                  "key": "46to55",
                  "from": "now-55y/y",
                  "to": "now-46y/y"
                },
                {
                  "key": "56to65",
                  "from": "now-65y/y",
                  "to": "now-56y/y"
                },
                {
                  "key": "gt65",
                  "to": "now-66y/y"
                }
              ]
            }
          }
        },
        "reportOptions": {},
      }
      let params = {
        "related-location": this.id,
        "_count": 10000000,
      }
      let totalUrl
      if(this.partOf){
        totalUrl = `/fhir/Practitioner?${querystring.stringify(params)}`
      }else{
        totalUrl = `/fhir/PractitionerRole?${querystring.stringify(params)}`
      }
      let {data} = await axios(totalUrl)
      this.totalStaff = data.total;
      let ageRangeUrl = `/es/jobposition/_search?filter_path=aggregations`

      let ageRangeResponse = await fetch(ageRangeUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(ageRangeQuery)
      })
      let ageRangeResult = await ageRangeResponse.json()

      let buckets = ageRangeResult.aggregations.age_range.buckets
      for (const key in buckets) {
        this.ageRangeData[key] = buckets[key]["doc_count"]
      }
      this.ageSeries = [
        {name: "By Age", data: Object.values(this.ageRangeData), groupColor: '#33b2df'}
      ];
    },
    buildWithData(series, categories) {
      this._buildGraphic(series, categories);
    },
    _buildGraphic(series, categories) {
      this.series = series;
      this.chartOptions = {
        chart: {
          type: "bar",
          height: 300,
          stacked: true,
          events: {
            mounted: (chartContext, config) => {
              setTimeout(() => {
                this.addAnnotations(config, categories, this);
              });
            },
            updated: (chartContext, config) => {
              setTimeout(() => {
                this.addAnnotations(config, categories, this);
              });
            }
          }
        },
        xaxis: {
          labels: {
            rotate: -45 //will rotate if label to long
          },
          categories: categories
        },
        title: {
          text: 'By Age',
          align: 'center',
          style: {
            fontSize: '14px',
            fontWeight: 'bold',
            fontFamily: undefined,
            color: '#263238'
          },
        },
      };
      const colors = series.map((x) => x.groupColor);
      if (colors.length > 0) {
        this.chartOptions.colors = colors;
      }
    },
    addAnnotations(config, categories, vm) {
      const seriesTotals = config.globals.stackedSeriesTotals;
      vm.$refs.genderChart.clearAnnotations();
      vm.$refs.professionChart.clearAnnotations();
      this.genderSeries = this.response ? Object.values(this.response.gender) : [0, 0];
      this.professionCategorySeries = this.response ? Object.values(this.professionCategory) : [10, 20, 30]
      vm.totalAll = 0;
      if (seriesTotals.length > 0) {
        vm.$refs.chart.clearAnnotations();
        categories.forEach((category, index) => {
          if (seriesTotals[index] > 0) {
            vm.totalAll += seriesTotals[index];

            vm.$refs.chart.addPointAnnotation({
              x: category,
              y: seriesTotals[index],
              label: {
                text: `${seriesTotals[index]}`
              }
            });
          }
        });
      }
    },
  }
}
</script>

<style>

</style>